import { cn } from '@/lib/utils';
import { Check, X, Info, AlertTriangle } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { ProgressRing } from '@/components/ui/ProgressRing';
import type { CriteriaMatch, UncheckableCriterion } from '@/types/eligibility';

interface MatchBadgeProps {
  match: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /** When provided, badge becomes clickable with a criteria breakdown popover */
  criteriaResults?: CriteriaMatch[];
  /** Whether the user meets all required criteria */
  eligible?: boolean;
  /** Anchor link to scroll to full eligibility details (e.g., "#eligibility") */
  detailLink?: string;
  /** Requirements that can't be verified from the profile */
  uncheckableCriteria?: UncheckableCriterion[];
  /** Match confidence level */
  confidence?: 'full' | 'partial';
  /** Blur the score to tease unauthenticated visitors */
  blurred?: boolean;
  /** Callback when a blurred badge is clicked (e.g., navigate to /auth) */
  onBlurClick?: () => void;
}

const sizeClasses = {
  sm: 'text-xs px-1.5 py-0.5',
  md: 'text-sm px-2 py-1',
  lg: 'text-base px-3 py-1.5',
};

/** Warm, teen-friendly language for match tiers */
function getMatchLabel(match: number, eligible: boolean, confidence: 'full' | 'partial'): {
  text: string;
  color: string;
} {
  if (!eligible) {
    if (match >= 30) return { text: 'Some gaps — review details', color: 'text-ds-red' };
    return { text: 'May not qualify', color: 'text-ds-red' };
  }
  if (match >= 90 && confidence === 'full') return { text: 'Great fit — go for it!', color: 'text-ds-green' };
  if (match >= 80 && confidence === 'full') return { text: 'Strong match!', color: 'text-ds-green' };
  if (match >= 80) return { text: 'Strong match — verify a few details', color: 'text-ds-green' };
  if (match >= 60) return { text: 'Worth a shot!', color: 'text-ds-gold' };
  if (match >= 40) return { text: 'Some criteria to check', color: 'text-ds-gray-2' };
  return { text: 'May not qualify', color: 'text-ds-red' };
}

const MatchBadge = ({
  match,
  size = 'md',
  className,
  criteriaResults,
  eligible = true,
  detailLink,
  uncheckableCriteria = [],
  confidence = 'full',
  blurred = false,
  onBlurClick,
}: MatchBadgeProps) => {
  const isHighMatch = match >= 90;
  const hasUncheckable = uncheckableCriteria.length > 0;
  const isIneligible = eligible === false;

  // ─── Blurred teaser badge for unauthenticated visitors ───
  if (blurred) {
    return (
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onBlurClick?.(); }}
        className={cn(
          'match-badge match-badge-medium font-bold rounded relative group/blur cursor-pointer',
          sizeClasses[size],
          className
        )}
        aria-label="Sign up to see your match score"
      >
        {/* Blurred percentage text */}
        <span className="blur-[5px] select-none" aria-hidden="true">
          {match}% Match
        </span>
        {/* "See match" overlay on hover */}
        <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/blur:opacity-100 transition-opacity text-[10px] font-semibold text-ds-gray-1 whitespace-nowrap">
          See match →
        </span>
      </button>
    );
  }

  const badgeContent = (
    <span
      className={cn(
        'match-badge font-bold rounded',
        isIneligible
          ? 'match-badge-ineligible bg-ds-red/15 text-ds-red'
          : isHighMatch ? 'match-badge-high' : 'match-badge-medium',
        sizeClasses[size],
        criteriaResults && 'cursor-pointer hover:opacity-90 transition-opacity',
        className
      )}
    >
      {isIneligible ? (
        <>
          <AlertTriangle className="w-3 h-3 inline-block mr-1" />
          Not Eligible
        </>
      ) : (
        <>{match}% Match</>
      )}
      {criteriaResults && <Info className="w-3 h-3 inline-block ml-1 opacity-60" />}
    </span>
  );

  // No criteria data — render as plain static badge
  if (!criteriaResults) {
    return badgeContent;
  }

  // Separate matches from misses
  const matched = criteriaResults.filter(c => c.matches);
  const unmatched = criteriaResults.filter(c => !c.matches);

  // Color for the progress ring
  const ringColor = match >= 80
    ? 'hsl(var(--ds-green))'
    : match >= 50
      ? 'hsl(var(--ds-gold))'
      : 'hsl(var(--ds-red))';

  const matchLabel = getMatchLabel(match, eligible, confidence);

  const handleDetailClick = (e: React.MouseEvent) => {
    if (!detailLink) return;
    e.preventDefault();
    const el = document.querySelector(detailLink);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        {badgeContent}
      </PopoverTrigger>
      <PopoverContent className="w-[calc(100vw-2rem)] sm:w-72 max-w-72 p-0" align="start" sideOffset={8}>
        {/* Header with progress ring */}
        <div className="flex items-center gap-3 p-4 pb-3 border-b border-ds-border">
          <ProgressRing progress={match / 100} size={44} strokeWidth={4} color={ringColor}>
            <span className="text-xs font-bold text-ds-gray-1">{match}%</span>
          </ProgressRing>
          <div>
            <p className="text-sm font-semibold text-ds-gray-1">Your Match Score</p>
            <p className={cn("text-xs font-medium", matchLabel.color)}>
              {matchLabel.text}
            </p>
          </div>
        </div>

        {/* Verified criteria list */}
        <div className="p-3 space-y-1.5 max-h-56 overflow-y-auto">
          {matched.map((c, i) => (
            <div key={`m-${i}`} className="flex items-start gap-2">
              <div className="w-4 h-4 rounded-full bg-ds-green/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Check className="w-2.5 h-2.5 text-ds-green" />
              </div>
              <span className="text-xs text-ds-gray-1 leading-snug">{c.label}</span>
            </div>
          ))}
          {unmatched.map((c, i) => (
            <div key={`u-${i}`} className="flex items-start gap-2">
              <div className="w-4 h-4 rounded-full bg-ds-red/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <X className="w-2.5 h-2.5 text-ds-red" />
              </div>
              <div>
                <span className="text-xs text-ds-gray-2 leading-snug">{c.label}</span>
                {c.required && (
                  <span className="text-[10px] text-ds-red ml-1 font-medium">Required</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Uncheckable criteria section — "Verify Yourself" */}
        {hasUncheckable && (
          <div className="px-3 pb-3 pt-1 border-t border-ds-border/50">
            <div className="flex items-center gap-1.5 mb-1.5">
              <AlertTriangle className="w-3 h-3 text-ds-gold" />
              <span className="text-[10px] font-semibold text-ds-gold uppercase tracking-wider">
                Verify Yourself
              </span>
            </div>
            <div className="space-y-1">
              {uncheckableCriteria.map((uc, i) => (
                <div key={`uc-${i}`} className="flex items-start gap-2">
                  <div className="w-4 h-4 rounded-full bg-ds-gold/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <AlertTriangle className="w-2.5 h-2.5 text-ds-gold" />
                  </div>
                  <div>
                    <span className="text-xs text-ds-gray-1 leading-snug">{uc.label}</span>
                    <p className="text-[10px] text-ds-gray-3 leading-snug">{uc.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer link to full details */}
        {detailLink && (
          <div className="border-t border-ds-border px-4 py-2.5">
            <button
              onClick={handleDetailClick}
              className="text-xs text-ds-red hover:underline font-medium w-full text-left"
            >
              See full eligibility details
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default MatchBadge;
